import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import dayjs from "dayjs";
import {
  ArrowLeft,
  Calendar,
  User,
  FileText,
  CreditCard,
  Plus,
  X,
  Calculator,
  Eye,
  Search,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Phone,
} from "lucide-react";
import InvoicePDF from "./Utility/InvoicePDF";
import { BlobProvider } from "@react-pdf/renderer";
import toast, { Toaster } from 'react-hot-toast';
import PatientSearchInput from "./ClinicComponent/PatientSearchInput";
import {
  useAddInvoiceMutation,
  useLazyGetbillingeditQuery,
  useLazyGetTestItemsQuery,
} from "../State/Api/InvoiceApiSlice";
import { DatePicker } from "rsuite";

function CreateInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceDropdownData = location.state?.invoiceDropdownData || [];
  const { appointmentID, patientUserUniqueID, userClinicId } = useParams();

const [triggerGetBillingEdit, { data: editInvoiceData }] =
  useLazyGetbillingeditQuery();

useEffect(() => {
  console.log("Route Params:", { appointmentID, patientUserUniqueID, userClinicId });

  if (appointmentID && patientUserUniqueID && userClinicId) {
    console.log("✅ Triggering Edit Invoice API");
    triggerGetBillingEdit({
      appointmentID,
      patientUserUniqueID,
      userClinicId,
    });
  } else {
    console.warn("⚠️ Missing Params — Skipping API Call");
  }
}, [appointmentID, patientUserUniqueID, userClinicId, triggerGetBillingEdit]);

 

  // Patient search and selection state
  const [patientSearchTerm, setPatientSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);

 
  const [
    triggerGetTestItems,
    { data: testItemsData, isFetching: isFetchingTests },
  ] = useLazyGetTestItemsQuery();

  useEffect(() => {
    const appointmentId = selectedPatient?.appointmentID;
    if (appointmentId) {
      triggerGetTestItems(appointmentId);
    }
  }, [selectedPatient, triggerGetTestItems]);
  const isEditMode = Boolean(appointmentID && patientUserUniqueID && userClinicId);

  const [addInvoice, { isLoading: isSubmitting }] = useAddInvoiceMutation();
  const [invoiceItems, setInvoiceItems] = useState([]);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("creditCard");
  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [dueDate, setDueDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );

  const patients = invoiceDropdownData;

  // Services dropdown data (would come from backend)
  // const [services] = useState([
  //   { id: 1, name: "General Consultation", priority: "High" },
  //   { id: 2, name: "Blood Test", priority: "Medium" },
  //   { id: 3, name: "X-Ray Chest", priority: "High" },
  //   { id: 4, name: "ECG", priority: "Critical" },
  //   { id: 5, name: "Vaccination", priority: "Low" },
  //   { id: 6, name: "Physical Therapy", priority: "Medium" },
  // ]);

  const services = (testItemsData || []).map((item, index) => ({
    id: index,
    name: item.testName,
    priority: item.priority || "Medium",
  }));

  // Clean gradient colors for different priorities
  const getPriorityGradient = (priority) => {
    const gradients = {
      Critical: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", // Red gradient
      High: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", // Orange gradient
      Medium: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)", // Blue gradient
      Low: "linear-gradient(135deg, #10b981 0%, #059669 100%)", // Green gradient
    };
    return (
      gradients[priority] || "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)"
    );
  };

  // Get priority text color
  const getPriorityTextColor = (priority) => {
    const colors = {
      Critical: "#dc2626",
      High: "#ea580c",
      Medium: "#2563eb",
      Low: "#059669",
    };
    return colors[priority] || "#4b5563";
  };

  // Get priority background color (lighter version for badges)
  const getPriorityBgColor = (priority) => {
    const backgrounds = {
      Critical: "#fef2f2",
      High: "#fff7ed",
      Medium: "#eff6ff",
      Low: "#f0fdf4",
    };
    return backgrounds[priority] || "#f9fafb";
  };

  const [serviceSearchTerms, setServiceSearchTerms] = useState({});
  const [showServiceDropdown, setShowServiceDropdown] = useState({});

  // Patient search functionality
  const filteredPatients = () => {
    if (!patientSearchTerm) return patients;

    const searchTerm = patientSearchTerm.toLowerCase();

    return patients.filter((patient) =>
      [
        patient.patientName,
        patient.patientIDUniqueID,
        patient.patientContactNumber,
        patient.age?.toString(),
        patient.gender,
      ]
        .filter(Boolean)
        .some((field) => field.toString().toLowerCase().includes(searchTerm))
    );
  };

const selectPatient = (patient) => {
  const fullPatient = invoiceDropdownData.find(
    (p) =>
      p.patientIDUniqueID === patient.patientId &&
      p.appointmentID === patient.appointmentId
  );

  if (!fullPatient) {
    toast.error("Patient data incomplete");
    return;
  }

  // Set patient search term as "NAME (Appt: 268)"
  setPatientSearchTerm(`${fullPatient.patientName}`);
  setSelectedPatient(fullPatient);
  setShowPatientDropdown(false);

  // Reset invoice items, etc.
  const newId = Date.now();
  setInvoiceItems([
    {
      id: newId,
      serviceId: "appointment",
      description: "Appointment",
      quantity: 1,
      unitPrice: fullPatient?.appointmentFee ?? 0,
      total: fullPatient?.appointmentFee ?? 0,
      disabled: true,
    },
  ]);
  setServiceSearchTerms({ [newId]: "Appointment" });
};



const clearPatientSelection = () => {
  setSelectedPatient(null);
  setPatientSearchTerm("");
  setInvoiceItems([]); 
  setServiceSearchTerms({}); 
};


  // Add this useEffect to handle outside clicks
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector("[data-search-container]");
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const prepareInvoiceData = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    const total = calculateTotal();

    return {
      invoiceNumber: "INV-001", // You can make this dynamic
      invoiceDate,
      dueDate,
      selectedPatient,
      invoiceItems,
      subtotal,
      tax,
      total,
      paymentTerms: "Due on receipt", // Get from form
      notes: "", // Get from form
    };
  };

const addInvoiceItem = () => {
  // Collect all selected (non-empty) service descriptions
  const selectedDescriptions = invoiceItems
    .map((item) => item.description)
    .filter(Boolean)
    .map((desc) => desc.toLowerCase());

  // Available backend services NOT already selected
  const availableServices = services.filter(
    (service) => !selectedDescriptions.includes(service.name.toLowerCase())
  );

  if (availableServices.length === 0) {
    toast.error("No available services to add.");
    return;
  }

  const newId = Date.now();

  // Add a blank new item without pre-selecting any service
  setInvoiceItems((prev) => [
    ...prev,
    {
      id: newId,
      serviceId: "",    
      description: "",   
      quantity: 1,
      unitPrice: 0,
      total: 0,
      disabled: false,
    },
  ]);

  // Clear the search input box for the new item service field
  setServiceSearchTerms((prev) => ({
    ...prev,
    [newId]: "",       // Empty to force user to select manually
  }));
};


  const removeInvoiceItem = (id) => {
    setInvoiceItems((prev) => prev.filter((item) => item.id !== id));
    setServiceSearchTerms((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const updateInvoiceItem = (id, field, value) => {
    setInvoiceItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          // auto recalc total if fields changed
          if (field === "quantity" || field === "unitPrice") {
            updated.total = updated.quantity * updated.unitPrice;
          }
          return updated;
        }
        return item;
      })
    );
  };

  // Updated selectService function with better error handling
  const selectService = (itemId, service) => {
    try {
      updateInvoiceItem(itemId, "serviceId", service.id);
      updateInvoiceItem(itemId, "description", service.name);

      // Close dropdown and update search term
      setShowServiceDropdown((prev) => ({
        ...prev,
        [itemId]: false,
      }));

      setServiceSearchTerms((prev) => ({
        ...prev,
        [itemId]: service.name,
      }));
    } catch (error) {
      console.error("Error selecting service:", error);
    }
  };

  // Add this function to clear service selection
  const clearServiceSelection = (itemId) => {
    updateInvoiceItem(itemId, "serviceId", "");
    updateInvoiceItem(itemId, "description", "");
    updateInvoiceItem(itemId, "unitPrice", 0);

    setServiceSearchTerms((prev) => ({
      ...prev,
      [itemId]: "",
    }));

    setShowServiceDropdown((prev) => ({
      ...prev,
      [itemId]: false,
    }));
  };

  const filteredServices = (searchTerm, itemId) => {
    const selectedDescriptions = invoiceItems
      .filter((item) => item.id !== itemId)
      .map((item) => (item.description || "").toLowerCase());
    return services.filter(
      (service) =>
        !selectedDescriptions.includes(service.name.toLowerCase()) &&
        service.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const calculateSubtotal = () => {
    return invoiceItems.reduce((sum, item) => sum + item.total, 0);
  };

  // const calculateTax = (subtotal) => {
  //   return subtotal * 0.18; // 18% GST for India
  // };
  const calculateTax = (subtotal) => {
    return 0; // 18% GST for India
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const tax = calculateTax(subtotal);
    return subtotal + tax;
  };

  const selectPaymentMethod = (method) => {
    setSelectedPaymentMethod(method);
  };

  const paymentMethods = [
    { id: "creditCard", label: "Credit Card", icon: CreditCard },
    { id: "bankTransfer", label: "Bank Transfer", icon: FileText },
    { id: "cash", label: "Cash", icon: User },
    { id: "insurance", label: "Insurance", icon: FileText },
  ];

  const [pdfUrl, setPdfUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    setGenerating(true);

    try {
      const invoiceData = prepareInvoiceData();

      // Import pdf function from @react-pdf/renderer
      const { pdf } = await import("@react-pdf/renderer");
      const blob = await pdf(<InvoicePDF invoiceData={invoiceData} />).toBlob();
      const url = URL.createObjectURL(blob);

      // Open in new tab
      window.open(url, "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setShowPatientDropdown]);


  const formik = useFormik({
    initialValues: {
      invoiceDate: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
      dueDate: null,
      invoiceType: "Consultation",
      referenceNumber: "",
      paymentType: "",
      amountPaid: 0,
      totalAmount: 0,
      notes: "",
      items: [
        {
          medicineName: "",
          testName: "",
          quantity: 1,
          unitPrice: 0,
        },
      ],
    },
    

    onSubmit: async (values) => {
      if (
  !selectedPatient ||
  !(
    selectedPatient.appointmentID ||
    selectedPatient.appointmentId
  ) ||
  !(
    selectedPatient.patientUserUniqueID ||
    selectedPatient.patientUserUniqueId
  )
) {
  alert("Please select a valid patient before submitting.");
  return;
}


      const subtotal = invoiceItems.reduce((sum, item) => sum + item.total, 0);
      const tax = 0; // As requested
      const total = subtotal + tax;

      const appointmentItem = invoiceItems.find(
  (item) => item.description?.toLowerCase() === "appointment"
);

     const payload = {
  appointmentID: selectedPatient?.appointmentID,
  patientUserUniqueID: selectedPatient?.patientUserUniqueID,
  doctorUserUniqueID: selectedPatient?.doctorUserUniqueID,
  userClinicID: localStorage.getItem("userClinicID"),
  invoiceDate: values.invoiceDate,
  dueDate: values.dueDate,
  invoiceType: values.invoiceType,
  referenceNumber: values.referenceNumber,
  appointmentFee: appointmentItem?.unitPrice || 0,
  amountPaid: 0,
  totalAmount: total,
  notes: values.notes,
  paymentType: values.paymentType,
  updatedBy: "admin",

  items: invoiceItems
    .filter(
      (item) =>
        item.unitPrice > 0 &&
        item.quantity > 0 &&
        item.description?.toLowerCase() !== "appointment"
    )
    .map((item) => ({
      invoiceNumber: "",
      medicineName: "",
      testName: item.description,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      totalPrice: item.total,
      subTotal: subtotal,
      taxAmount: tax,
      discountAmount: 0,
      totalAmount: total,
    })),
};

      try {
      const result =  await addInvoice(payload).unwrap();
        if(result.status !== "100"){
         toast.success("Submitted successfully");
        navigate("/Billing");
      }
      else{ 
        toast.warning("No changes made to submit");
      }
    }
      catch (error) {
        console.error("Submission error:", error);
        toast.error("Submission failed");
      }
    },
  });

 useEffect(() => {
  if (editInvoiceData) {
    console.log("Editing Invoice Data:", editInvoiceData);

    // 1️⃣ Bind Formik fields
    formik.setValues({
      patientUserUniqueID: editInvoiceData.clinicAndPatientInfo?.patientUserUniqueID ?? "",
      patientName: editInvoiceData.clinicAndPatientInfo?.patientName ?? "",
      invoiceDate: editInvoiceData.invoiceHeader?.invoiceDate ?? "",
      dueDate: editInvoiceData.invoiceHeader?.dueDate ?? "",
      invoiceType: editInvoiceData.invoiceHeader?.invoiceType ?? "",
      referenceNumber: editInvoiceData.invoiceHeader?.referenceNumber ?? "",
      paymentType: editInvoiceData.invoiceHeader?.paymentType ?? "",
      amountPaid: editInvoiceData.invoiceHeader?.amountPaid ?? 0,
      totalAmount: editInvoiceData.invoiceHeader?.totalAmount ?? 0,
      notes: editInvoiceData.invoiceHeader?.notes ?? "",
      items:
        editInvoiceData.invoiceItems?.map((item) => ({
          medicineName: item.medicineName ?? "",
          testName: item.testName ?? "",
          quantity: item.quantity ?? 1,
          unitPrice: item.unitPrice ?? 0,
        })) ?? [],
    });

    // 2️⃣ Bind Patient to sidebar card (PatientSearchInput)
    if (editInvoiceData?.clinicAndPatientInfo && editInvoiceData?.invoiceHeader) {
  const p = editInvoiceData.clinicAndPatientInfo;
  const h = editInvoiceData.invoiceHeader;

  setSelectedPatient({
    patientName: p.patientName,
    patientUserUniqueID: p.patientUserUniqueID,  // from patient info
    patientIDUniqueID: p.patientIDUniqueID,
    appointmentID: h.appointmentID,             // ✅ from invoiceHeader
    doctorUserUniqueID: h.doctorUserUniqueID,   // ✅ from invoiceHeader
    userClinicID: h.userClinicID,               // ✅ from invoiceHeader
    patientContactNumber: p.patientContactNumber,
    age: p.age,
    gender: p.gender,
    address: p.patientAddress || p.address,
    allergies: p.allergies_ifany
      ? p.allergies_ifany.split(",").map((a) => a.trim())
      : [],
    conditions: p.medicalConditions || "",
    dateOfBirth: p.patienentDatefBirth || "",
    clinicName: p.clinicName,
    clinicLicenseID: p.clinicLicenseID,
    clinicAddress: p.clinicAddress,
    clinicContactNumber: p.clinicContactNumber,
  });
}


    // 3️⃣ Bind invoice items to UI
    const mappedItems =
      editInvoiceData.invoiceItems?.map((item, index) => ({
        id: index + 1,
        serviceId: item.invoiceItemID,
        description: item.testName || item.medicineName || "Service",
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.totalPrice,
        disabled: false,
      })) ?? [];

    setInvoiceItems(mappedItems);

    // 4️⃣ Populate service search box text
    const serviceTerms = {};
    mappedItems.forEach((item) => {
      serviceTerms[item.id] = item.description;
    });
    setServiceSearchTerms(serviceTerms);
  }
}, [editInvoiceData]);



  // Map current form data to PDF shape
  const prepareInvoiceDataForPDF = () => {
    // Extract from selectedPatient and invoiceItems as well as formik.values
    return {
      clinicName: localStorage.getItem("clinicName") || "Clinic Name",
      invoiceNumber: formik.values.referenceNumber || "INV-001",
         invoiceDate: dayjs(formik.values.invoiceDate).format("DD-MMM-YYYY"),
    dueDate: dayjs(formik.values.dueDate).format("DD-MMM-YYYY"),
      patientName: selectedPatient?.patientName || "",
      patientId: selectedPatient?.patientUserUniqueID || "",
      patientDOB: selectedPatient?.patienentDatefBirth
        ? new Date(selectedPatient.patienentDatefBirth).toLocaleDateString(
            "en-GB",
            { day: "2-digit", month: "short", year: "numeric" }
          )
        : "N/A",
      patientPhone: selectedPatient?.patientContactNumber || "",
      patientAddress: selectedPatient?.patientAddress || "",
      doctorName: selectedPatient?.assignedDoctorName || "",
      invoiceItems: invoiceItems.map((item) => ({
        description: item.description,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        total: item.total,
        gst: 0, // Or whatever source
      })),
      subtotal: calculateSubtotal(),
      tax: calculateTax(),
      total: calculateTotal(),
      paymentType: formik.values.paymentType,
      amountPaid: formik.values.amountPaid,
      notes: formik.values.notes,
         appointmentFee:
      invoiceItems.length > 0 && invoiceItems[0].description === "Appointment"
        ? invoiceItems[0].unitPrice
        : 0,
    };
  };

    // Function to add a new procedure item
const addProcedureItem = () => {
  const newId = Date.now(); // Generate unique ID
  const newProcedureItem = {
    id: newId,
    description: "Procedure",
    quantity: 1,
    unitPrice: 0,
    total: 0,
    disabled: true, // Make it non-editable like appointment
  };
  
  setInvoiceItems([...invoiceItems, newProcedureItem]);
  
  // Set the search term for the new procedure item
  setServiceSearchTerms((prev) => ({
    ...prev,
    [newId]: "Procedure",
  }));
  
  // Don't show dropdown for procedure items
  setShowServiceDropdown((prev) => ({
    ...prev,
    [newId]: false,
  }));
};


  const datestyles = `

  .rs-input-group{
  border-radius: 12px;
  height: 50px;
  border-color:1px solid #e5e5ea;
  }

`;

  return (
    <>
      <style>{datestyles}</style>
      <div
        style={{
          minHeight: "100vh",
          // background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
          padding: "24px",
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-12 h-12 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {editInvoiceData?'Update Invoice':"Create Invoice"}
              </h1>
              <p className="text-gray-600">
                Generate a new invoice for patient billing
              </p>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "32px",
            }}
          >
            {/* Main Content */}

            <div style={{ gridColumn: "span 2" }}>
              {/* Invoice Details Card */}
              <div
                style={{
                  background: "white",
                  borderRadius: "20px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #f3f4f6",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FileText
                      style={{ width: "20px", height: "20px", color: "white" }}
                    />
                  </div>
                  <h2
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      color: "#1f2937",
                    }}
                  >
                    Invoice Details
                  </h2>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "24px",
                    marginBottom: "24px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Invoice Date
                    </label>
                  
                    <DatePicker
                      name="invoiceDate"
                      value={
                        formik.values.invoiceDate
                          ? new Date(formik.values.invoiceDate)
                          : null
                      }
                      onChange={(value) =>
                        formik.setFieldValue("invoiceDate", value)
                      }
                      onBlur={() => formik.setFieldTouched("invoiceDate", true)}
                      format="dd-MMM-yyyy"
                      placeholder="dd-mmm-yyyy"
                      oneTap
                      style={{ width: "100%" }}
                      className="custom-datepicker"
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Due Date
                    </label>
                    <DatePicker
                      name="dueDate"
                      value={
                        formik.values.dueDate
                          ? new Date(formik.values.dueDate)
                          : null
                      }
                      onChange={(value) =>
                        formik.setFieldValue("dueDate", value)
                      }
                      onBlur={() => formik.setFieldTouched("dueDate", true)}
                      format="dd-MMM-yyyy"
                      placeholder="dd-mmm-yyyy"
                      oneTap
                      style={{ width: "100%" }}
                      className="custom-datepicker"
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: "24px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Invoice Type
                    </label>
                    <select
                      name="invoiceType"
                      value={formik.values.invoiceType}
                      onChange={formik.handleChange}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "12px",
                        fontSize: "14px",
                        outline: "none",
                        background: "white",
                      }}
                    >
                      <option value="">Select type</option>
                      <option value="consultation">Outpatient(OPD) Invoice</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Reference Number (Optional)
                    </label>
                    <input
                      type="text"
                      name="referenceNumber"
                      value={formik.values.referenceNumber}
                      onChange={formik.handleChange}
                      placeholder="REF-12345"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "12px",
                        fontSize: "14px",
                        outline: "none",
                        transition: "all 0.2s",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Items & Services Card */}

              <div
                style={{
                  background: "white",
                  borderRadius: "20px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #f3f4f6",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "20px", // Reduced from 24px
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Calculator
                        style={{
                          width: "20px",
                          height: "20px",
                          color: "white",
                        }}
                      />
                    </div>
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "600",
                        color: "#1f2937",
                      }}
                    >
                      Items & Services
                    </h2>
                  </div>
                  <div style={{display:'flex',gap:15}}>
                   <button
                    onClick={addProcedureItem}
                    style={{
                      padding: "10px 16px",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "white",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "500",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                      transition: "all 0.2s",
                    }}
                  >
                    <Plus style={{ width: "16px", height: "16px" }} />
                    Add Procedure
                  </button>
                  <button
                    onClick={addInvoiceItem}
                    style={{
                      padding: "10px 16px",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      color: "white",
                      borderRadius: "12px",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontWeight: "500",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                      transition: "all 0.2s",
                    }}
                  >
                    <Plus style={{ width: "16px", height: "16px" }} />
                    Add Item
                  </button>
                  </div>
                </div>

                <div>
                  {/* Header with Indian Rupee */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr 60px",
                      gap: "16px",
                      padding: "10px 16px", // Reduced padding
                      background: "#f9fafb",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#6b7280",
                    }}
                  >
                    <div>Service</div>
                    <div>Quantity</div>
                    <div>Unit Price (₹)</div>
                    <div>Total (₹)</div>
                    <div></div>
                  </div>

                  {/* Items with Fixed Dropdown and Rupee Symbol */}
                  {invoiceItems.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr 60px",
                        gap: "16px",
                        alignItems: "center",
                        padding: "14px 16px",
                        border: "1px solid #f3f4f6",
                        borderRadius: "12px",
                        marginBottom: "10px",
                      }}
                    >
                      {/* Service field */}
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          placeholder="Search for service..."
                          value={serviceSearchTerms[item.id] || ""}
                          onChange={(e) => {
                            setServiceSearchTerms((prev) => ({
                              ...prev,
                              [item.id]: e.target.value,
                            }));
                            setShowServiceDropdown((prev) => ({
                              ...prev,
                              [item.id]: true,
                            }));
                          }}
                          onFocus={() =>
                            setShowServiceDropdown((prev) => ({
                              ...prev,
                              [item.id]: true,
                            }))
                          }
                          disabled={
              item.description === "Appointment" || 
              item.description === "Procedure" || 
              item.disabled
            }
                          style={{
                            width: "100%",
                            padding: "10px 70px 10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                            transition: "all 0.2s",
                            background:
                item.description === "Appointment" ||
                item.description === "Procedure" ||
                item.disabled
                  ? "#f3f3f3"
                  : "#fff",
                            fontWeight:
                              item.description === "Appointment" ||
                              item.disabled
                                ? "600"
                                : "400",
                          }}
                        />

                        {/* (Clear and Search icons, etc, as before) */}

                        {/* Dropdown */}
                        {showServiceDropdown[item.id] && !item.disabled && (
                          <div
                            data-dropdown={item.id}
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              background: "white",
                              border: "1px solid #d1d5db",
                              borderRadius: "12px",
                              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
                              zIndex: 1000,
                              maxHeight: "220px",
                              overflowY: "auto",
                              marginTop: "4px",
                            }}
                          >
                            {filteredServices(
                              serviceSearchTerms[item.id] || "",
                              item.id
                            ).length > 0 ? (
                              filteredServices(
                                serviceSearchTerms[item.id] || "",
                                item.id
                              ).map((service) => (
                                <div
                                  key={service.id}
                                  onClick={() =>
                                    selectService(item.id, service)
                                  }
                                  style={{
                                    padding: "14px 16px",
                                    cursor: "pointer",
                                    borderBottom: "1px solid #f1f3f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    background: getPriorityBgColor(
                                      service.priority
                                    ), // <-- restores colored bg
                                    color: getPriorityTextColor(
                                      service.priority
                                    ), // <-- text color
                                    transition:
                                      "background 0.25s, transform 0.2s", // <-- smooth transition
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background =
                                      getPriorityBgColor(service.priority);
                                    e.currentTarget.style.transform =
                                      "translateX(4px)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                      getPriorityBgColor(service.priority);
                                    e.currentTarget.style.transform =
                                      "translateX(0)";
                                  }}
                                >
                                  {/* colorful left bar */}
                                  <div
                                    style={{
                                      width: "4px",
                                      height: "24px",
                                      borderRadius: "2px",
                                      background: getPriorityGradient(
                                        service.priority
                                      ),
                                      flexShrink: 0,
                                      marginRight: "12px",
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontWeight: 500,
                                      fontSize: "14px",
                                      flex: 1,
                                    }}
                                  >
                                    {service.name}
                                  </span>
                                  {/* priority badge */}
                                  <div
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "20px",
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      background: getPriorityGradient(
                                        service.priority
                                      ),
                                      color: "white",
                                      minWidth: "60px",
                                      textAlign: "center",
                                      marginLeft: "12px",
                                    }}
                                  >
                                    {service.priority}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div
                                style={{
                                  padding: "16px",
                                  fontSize: "14px",
                                  color: "#6b7280",
                                  textAlign: "center",
                                  fontStyle: "italic",
                                }}
                              >
                                No services found
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Quantity */}
                      <div>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            updateInvoiceItem(
                              item.id,
                              "quantity",
                              parseInt(e.target.value) || 1
                            )
                          }
                          disabled={
                            item.description === "Appointment" || item.disabled
                          }
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                            background:
                              item.description === "Appointment" ||
                              item.disabled
                                ? "#f3f3f3"
                                : "#fff",
                            fontWeight:
                              item.description === "Appointment" ||
                              item.disabled
                                ? "600"
                                : "400",
                          }}
                        />
                      </div>

                      {/* Unit Price */}
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            position: "absolute",
                            left: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: 14,
                            color: "#6b7280",
                          }}
                        >
                          ₹
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) =>
                            updateInvoiceItem(
                              item.id,
                              "unitPrice",
                              parseFloat(e.target.value)
                            )
                          }
                          // allow editing for all, including appointment
                          style={{
                            width: "100%",
                            padding: "10px 12px 10px 28px",
                            border: "1px solid #d1d5db",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                          }}
                        />
                      </div>

                      {/* Total */}
                      <div>
                        <div
                          style={{
                            padding: "10px 12px",
                            background: "#f9fafb",
                            borderRadius: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                            border: "1px solid #f3f4f6",
                          }}
                        >
                          ₹{(item.total || 0).toFixed(2)}
                        </div>
                      </div>

                      {/* Remove button (don't show for appointment/first row if you want) */}
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {invoiceItems.length > 1 && (
                          <button
                            onClick={() => removeInvoiceItem(item.id)}
                            style={{
                              padding: "8px",
                              color: "#9ca3af",
                              background: "transparent",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                            }}
                          >
                            <X style={{ width: "16px", height: "16px" }} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Updated Totals with Indian Rupees and GST */}
                  <div
                    style={{
                      borderTop: "2px solid #e5e7eb",
                      paddingTop: "16px",
                      marginTop: "20px", // Reduced margin
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "8px",
                        fontSize: "14px",
                      }}
                    >
                      <span style={{ color: "#6b7280" }}>Subtotal:</span>
                      <span style={{ fontWeight: "600", color: "#374151" }}>
                        ₹{calculateSubtotal().toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                        fontSize: "14px",
                      }}
                    >
                      <span style={{ color: "#6b7280" }}>Tax (0% GST):</span>
                      <span style={{ fontWeight: "600", color: "#374151" }}>
                        {/* ₹{(calculateSubtotal() * 0.18).toFixed(2)} */}₹
                        {calculateTax(calculateSubtotal()).toFixed(2)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "18px",
                        fontWeight: "bold",
                        borderTop: "1px solid #e5e7eb",
                        paddingTop: "16px",
                      }}
                    >
                      <span style={{ color: "#1f2937" }}>Total:</span>
                      <span style={{ color: "#10b981" }}>
                        {/* ₹{(calculateSubtotal() * 1.18).toFixed(2)} */}₹
                        {calculateTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information Card */}
              <div
                style={{
                  background: "white",
                  borderRadius: "20px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #f3f4f6",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <h2
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "16px",
                  }}
                >
                  Additional Information
                </h2>
                <div style={{ display: "grid", gap: "24px" }}>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      rows="4"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      placeholder="Enter any additional notes or instructions"
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "12px",
                        fontSize: "14px",
                        outline: "none",
                        resize: "none",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                        marginBottom: "8px",
                      }}
                    >
                      Payment Type
                    </label>
                    <select
                      name="paymentType"
                      value={formik.values.paymentType}
                      onChange={formik.handleChange}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "12px",
                        fontSize: "14px",
                        outline: "none",
                        background: "white",
                      }}
                    >
                     <option value="" disabled>Select Payment Type</option>
                      <option value="OFFLINE">Offline</option>
                      <option value="ONLINE">Online</option>

                     
                    </select>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                <button
                onClick={()=> navigate(-1)}
                  style={{
                    padding: "12px 24px",
                    color: "#6b7280",
                    background: "#f3f4f6",
                    border: "none",
                    borderRadius: "12px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <BlobProvider
                  document={
                    <InvoicePDF invoiceData={prepareInvoiceDataForPDF()} />
                  }
                >
                  {({ url, loading, error }) => {
                    if (error) {
                      console.error("PDF generation error:", error);
                      return (
                        <button
                          style={{
                            padding: "12px 24px",
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "12px",
                            fontWeight: "500",
                            cursor: "not-allowed",
                          }}
                        >
                          PDF Error
                        </button>
                      );
                    }

                    return (
                      <button
                        onClick={() => {
                          if (url && !loading) {
                            window.open(url, "_blank");
                          }
                        }}
                        disabled={loading}
                        style={{
                          padding: "12px 24px",
                          background: loading
                            ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                            : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                          color: "white",
                          border: "none",
                          borderRadius: "12px",
                          fontWeight: "500",
                          cursor: loading ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          boxShadow: "0 4px 12px rgba(107, 114, 128, 0.3)",
                          opacity: loading ? 0.7 : 1,
                        }}
                      >
                        <Eye style={{ width: "16px", height: "16px" }} />
                        {loading ? "Generating PDF..." : "Preview Invoice"}
                      </button>
                    );
                  }}
                </BlobProvider>
               <button
  type="submit"
  onClick={formik.handleSubmit}
  disabled={isSubmitting} 
  style={{
    padding: "12px 24px",
    background: isSubmitting
      ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)" // greyed out when submitting
      : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    fontWeight: "500",
    cursor: isSubmitting ? "not-allowed" : "pointer",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
  }}
>
  {isSubmitting ? "Submitting..." : "Create Invoice"}
</button>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              {/* Updated Patient Information Card with Green Gradient */}

              <PatientSearchInput
                patients={patients}
                onPatientSelect={selectPatient}
                selectedPatient={selectedPatient}
                onPatientClear={clearPatientSelection}
                isEditMode={isEditMode}
                editInvoiceData={editInvoiceData}
                setSelectedPatient={setSelectedPatient}
              />

              <div style={{ maxWidth: "600px", margin: "0 auto" }}>
                {/* Insurance Information Card */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #f3f4f6",
                    padding: "24px",
                    marginBottom: "24px",
                  }}
                >
                  {/* Card Header */}
                  <h3
                    style={{
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#1f2937",
                      marginBottom: "16px",
                    }}
                  >
                    Insurance Information
                  </h3>

                  {/* Coming Soon Insurance Illustration */}
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      background:
                        "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
                      borderRadius: "12px",
                      border: "2px dashed #bae6fd",
                    }}
                  >
                    {/* Insurance Shield Icon */}
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        background:
                          "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        boxShadow: "0 6px 20px rgba(2, 132, 199, 0.3)",
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                          stroke="white"
                          strokeWidth="2"
                          fill="white"
                          fillOpacity="0.2"
                        />
                      </svg>
                    </div>

                    <h4
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#0369a1",
                        marginBottom: "8px",
                      }}
                    >
                      Coming Soon
                    </h4>

                    <p
                      style={{
                        fontSize: "14px",
                        color: "#0284c7",
                        opacity: "0.8",
                      }}
                    >
                      Insurance verification system under development
                    </p>
                  </div>
                </div>

                {/* Payment Methods Card */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #f3f4f6",
                    padding: "24px",
                  }}
                >
                  {/* Payment Method Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "16px",
                    }}
                  >
                    {/* Payment Icon */}
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        background:
                          "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span
                        style={{
                          color: "white",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        $
                      </span>
                    </div>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#1f2937",
                      }}
                    >
                      Payment Method
                    </h3>
                  </div>

                  {/* Coming Soon Payment Illustration */}
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      background:
                        "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                      borderRadius: "12px",
                      border: "2px dashed #bbf7d0",
                    }}
                  >
                    {/* Payment Card Icon */}
                    <div
                      style={{
                        width: "60px",
                        height: "60px",
                        background:
                          "linear-gradient(135deg, #059669 0%, #047857 100%)",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 16px",
                        boxShadow: "0 6px 20px rgba(5, 150, 105, 0.3)",
                      }}
                    >
                      <CreditCard
                        style={{
                          width: "24px",
                          height: "24px",
                          color: "white",
                        }}
                      />
                    </div>

                    <h4
                      style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        color: "#047857",
                        marginBottom: "8px",
                      }}
                    >
                      Coming Soon
                    </h4>

                    <p
                      style={{
                        fontSize: "14px",
                        color: "#059669",
                        opacity: "0.8",
                      }}
                    >
                      Secure payment processing system in development
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
           <Toaster />
    </>
  );
}

export default CreateInvoice;
